/**
 * ReportViewer — react-grid-layout read-only display of a report's widgets.
 *
 * Kicks off a single /execute call when the spec id is available, caches
 * the result keyed by report id into sessionStorage so the second visit
 * renders instantly (Fork 4 DoD requires <1s perceived latency on second
 * visit). Cache TTL is 2 minutes — the executor itself is a SELECT, not
 * free, so we don't want to re-run on every back/forward nav.
 */

import { useEffect, useMemo, useState } from 'react'
import { Responsive, WidthProvider, type Layout } from 'react-grid-layout'

import type { ReportFull, ReportExecutionResult } from './types'
import { WidgetRenderer } from './WidgetRenderer'

import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

const ResponsiveGridLayout = WidthProvider(Responsive)

const CACHE_TTL_MS = 2 * 60 * 1000
const cacheKey = (id: number | string) => `butler_report_exec_${id}`

interface ReportViewerProps {
  report: ReportFull
  /** Caller can force a refresh via a monotonic version bump. */
  refreshKey?: number
}

interface CachedExec {
  at: number
  result: ReportExecutionResult
}

function readCache(id: number | string): ReportExecutionResult | null {
  try {
    const raw = window.sessionStorage?.getItem(cacheKey(id))
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedExec
    if (Date.now() - parsed.at > CACHE_TTL_MS) return null
    return parsed.result
  } catch {
    return null
  }
}

function writeCache(id: number | string, result: ReportExecutionResult) {
  try {
    const payload: CachedExec = { at: Date.now(), result }
    window.sessionStorage?.setItem(cacheKey(id), JSON.stringify(payload))
  } catch {
    // Quota exceeded or privacy mode — non-fatal; next visit re-executes.
  }
}

export function ReportViewer({ report, refreshKey }: ReportViewerProps) {
  const [execution, setExecution] = useState<ReportExecutionResult | null>(() => readCache(report.id))
  const [loading, setLoading] = useState<boolean>(execution === null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const cached = readCache(report.id)
    if (cached && !refreshKey) {
      setExecution(cached)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    fetch(`/api/reports/${report.id}/execute`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`execute failed: ${res.status}`)
        return (await res.json()) as ReportExecutionResult
      })
      .then((result) => {
        if (cancelled) return
        setExecution(result)
        writeCache(report.id, result)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : String(err))
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [report.id, refreshKey])

  const layouts = useMemo<{ lg: Layout[] }>(() => {
    return {
      lg: report.spec.widgets.map((w) => ({
        i: w.id,
        x: w.layout.x,
        y: w.layout.y,
        w: w.layout.w,
        h: w.layout.h,
        static: true,
      })),
    }
  }, [report.spec.widgets])

  const resultById = useMemo(() => {
    const map = new Map<string, ReportExecutionResult['widgets'][0]>()
    for (const w of execution?.widgets ?? []) map.set(w.widgetId, w)
    return map
  }, [execution])

  return (
    <div data-testid="report-viewer" className="relative">
      {loading && (
        <div className="mb-3 text-xs text-slate-500 dark:text-slate-400">
          Loading report…
        </div>
      )}
      {error && (
        <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200">
          Couldn't run report: {error}
        </div>
      )}
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 1024, md: 768, sm: 0 }}
        cols={{ lg: 12, md: 10, sm: 6 }}
        rowHeight={60}
        isDraggable={false}
        isResizable={false}
        margin={[12, 12]}
      >
        {report.spec.widgets.map((w) => (
          <div key={w.id}>
            <WidgetRenderer widget={w} result={resultById.get(w.id)} />
          </div>
        ))}
      </ResponsiveGridLayout>
    </div>
  )
}
